import {
  OrderPaymentType,
  OrderPaymentStatusType,
} from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import { PaidBadge } from "../../../components";
import {
  Badge,
  BadgeGreen,
  BadgeRed,
  BadgeIndigo,
  BadgeZinc,
  BadgeOrange,
  BadgeCyan,
} from "../../../components/badge/Badge";
import { RefundedBadge } from "../../../components/badge/RefundedBadge";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { TooltipContainer } from "@sellar-io/components";

const generateDraftInvoiceToolTip = (
  invoiceCreationStage: "confirmed" | "dispatched" | "delivered",
) => {
  if (invoiceCreationStage === "confirmed") {
    return "Issued on finalisation";
  }

  if (invoiceCreationStage === "dispatched") {
    return "Issued on dispatch";
  }

  return "Issued on delivery";
};

export default function PaymentStatusIndicator({
  paymentStatus,
  voidedAt,
  paidAt,
  retailerPaidAt,
  paymentType,
  paymentDue,
  cancelledAt,
  className,
  invoiceIssuedAt,
  invoicesEnabled,
  showOverdueStatus,
  iconOnly = false,
}: {
  paymentStatus: OrderPaymentStatusType | null;
  voidedAt: Date | null;
  paidAt: Date | null;
  retailerPaidAt: Date | null;
  paymentType: OrderPaymentType | null;
  paymentDue: Date | null;
  invoiceIssuedAt: Date | null;
  cancelledAt: Date | null;
  className?: string;
  invoicesEnabled: boolean;
  showOverdueStatus?: boolean;
  iconOnly?: boolean;
}): ReactElement | null {
  const invoicesDisabled = paymentType !== "stripe" && !invoicesEnabled;
  const { business } = useSellarCurrentBusiness();

  const orderPaid = business?.Retailer ? !!retailerPaidAt : !!paidAt;
  const draftInvoiceToolTipText = business?.Supplier?.invoiceCreationStage
    ? generateDraftInvoiceToolTip(business.Supplier.invoiceCreationStage)
    : null;

  if (iconOnly) {
    if (paymentStatus === "refunded") {
      return (
        <TooltipContainer enable={true} text="Payment refunded">
          <BadgeIndigo icon={"ARROW_U_LEFT_TOP"} />
        </TooltipContainer>
      );
    }

    if (
      (paymentStatus === "void" &&
        voidedAt &&
        invoicesEnabled &&
        !invoicesDisabled) ||
      (cancelledAt && paymentStatus !== "paid" && !invoicesDisabled)
    ) {
      return (
        <TooltipContainer enable={true} text="Invoice voided">
          <BadgeZinc icon={"FILE_DOCUMENT_REMOVE_OUTLINE"} />
        </TooltipContainer>
      );
    }

    if (orderPaid) {
      return (
        <TooltipContainer enable={true} text="Invoice paid">
          <BadgeGreen
            className="print:hidden"
            icon={"FILE_DOCUMENT_CHECK_OUTLINE"}
          />
        </TooltipContainer>
      );
    }

    if (
      dayjs(paymentDue).isBefore(dayjs(), "day") &&
      !cancelledAt &&
      !voidedAt &&
      !invoicesDisabled
    ) {
      const isPaymentDueLessThan100Days =
        dayjs().diff(dayjs(paymentDue), "day") <= 100;
      const isSupplierOrNotManualPayment =
        business?.Supplier || paymentType !== "manual" || showOverdueStatus;
      const showInvoiceOverdue =
        isSupplierOrNotManualPayment && isPaymentDueLessThan100Days;

      return showInvoiceOverdue ? (
        <TooltipContainer enable={true} text="Invoice overdue">
          <BadgeRed icon={"FILE_DOCUMENT_ALERT_OUTLINE"} />
        </TooltipContainer>
      ) : null;
    }

    if (
      paymentDue &&
      paymentStatus === "draft" &&
      !cancelledAt &&
      paymentType === "stripe"
    ) {
      return (
        <TooltipContainer
          enable={!!draftInvoiceToolTipText?.length}
          text={draftInvoiceToolTipText ?? ""}
        >
          <BadgeOrange icon={"CREDIT_CARD_EDIT_OUTLINE"} />
        </TooltipContainer>
      );
    }

    if (
      (invoiceIssuedAt && !cancelledAt && !invoicesDisabled) ||
      paymentType === "checkout"
    ) {
      return (
        <TooltipContainer
          enable={true}
          text={business?.Retailer ? "Invoice available" : "Invoice issued"}
        >
          <BadgeCyan icon={"FILE_DOCUMENT_ARROW_RIGHT_OUTLINE"} />
        </TooltipContainer>
      );
    }

    if (paymentType === "stripe") {
      if (paymentStatus === "draft" && !cancelledAt) {
        return (
          <TooltipContainer
            enable={!!draftInvoiceToolTipText?.length}
            text={draftInvoiceToolTipText ?? ""}
          >
            <BadgeOrange icon={"NOTE_ALT_OUTLINE"} />
          </TooltipContainer>
        );
      }

      if (paymentStatus === "open" && !cancelledAt) {
        return (
          <TooltipContainer enable={true} text="Invoice sent">
            <Badge
              className={twMerge("text-secondary print:hidden", className)}
              style={{ backgroundColor: "#cffafe", color: "#155e75" }}
              icon={"CREDIT_CARD_FAST_OUTLINE"}
            />
          </TooltipContainer>
        );
      }
    }
  } else {
    if (paymentStatus === "refunded") {
      return <RefundedBadge />;
    }

    if (
      (paymentStatus === "void" &&
        voidedAt &&
        invoicesEnabled &&
        !invoicesDisabled) ||
      (cancelledAt && paymentStatus !== "paid" && !invoicesDisabled)
    ) {
      return (
        <BadgeZinc icon={"FILE_DOCUMENT_REMOVE_OUTLINE"}>
          Invoice voided
        </BadgeZinc>
      );
    }

    if (orderPaid) {
      return <PaidBadge className={twMerge("print:hidden", className)} />;
    }

    if (
      dayjs(paymentDue).isBefore(dayjs(), "day") &&
      !cancelledAt &&
      !voidedAt &&
      !invoicesDisabled
    ) {
      const isPaymentDueLessThan100Days =
        dayjs().diff(dayjs(paymentDue), "day") <= 100;
      const isSupplierOrNotManualPayment =
        business?.Supplier || paymentType !== "manual" || showOverdueStatus;
      const showInvoiceOverdue =
        isSupplierOrNotManualPayment && isPaymentDueLessThan100Days;

      return showInvoiceOverdue ? (
        <BadgeRed icon={"FILE_DOCUMENT_ALERT_OUTLINE"}>
          Invoice overdue
        </BadgeRed>
      ) : null;
    }

    if (
      paymentDue &&
      paymentStatus === "draft" &&
      !cancelledAt &&
      paymentType === "stripe"
    ) {
      return (
        <TooltipContainer
          enable={!!draftInvoiceToolTipText?.length}
          text={draftInvoiceToolTipText ?? ""}
        >
          <BadgeOrange icon={"NOTE_ALT_OUTLINE"}>Draft invoice</BadgeOrange>
        </TooltipContainer>
      );
    }

    if (
      (invoiceIssuedAt && !cancelledAt && !invoicesDisabled) ||
      paymentType === "checkout"
    ) {
      return (
        <BadgeCyan icon={"FILE_DOCUMENT_ARROW_RIGHT_OUTLINE"}>
          {business?.Retailer ? "Invoice available" : "Invoice issued"}
        </BadgeCyan>
      );
    }

    if (paymentType === "stripe") {
      if (paymentStatus === "draft" && !cancelledAt) {
        return (
          <TooltipContainer
            enable={!!draftInvoiceToolTipText?.length}
            text={draftInvoiceToolTipText ?? ""}
          >
            <BadgeOrange icon={"NOTE_ALT_OUTLINE"}>Draft invoice</BadgeOrange>
          </TooltipContainer>
        );
      }

      if (paymentStatus === "open" && !cancelledAt) {
        return (
          <Badge
            className={twMerge("text-secondary print:hidden", className)}
            style={{ backgroundColor: "#cffafe", color: "#155e75" }}
            icon={"CREDIT_CARD_FAST_OUTLINE"}
          >
            Invoice sent
          </Badge>
        );
      }
    }
  }
  return null;
}
